import { createContext, useEffect, useState } from "react";

import { getItemFromStore, setItemToStore } from "../helpers/utils";
import { isBrandSetupPending } from "../helpers/brand-setup";
import { BRAND_ENDPOINTS, PUBLISHER_ENDPOINTS } from "../constants/endpoints";
import { PAYMENT_MODEL } from "../constants/panel/common";
import useAxios from "../hooks/use-axios";

// ** Create Context
const UserContext = createContext({
  userData            : { user: {} },
  handleUserData      : () => {},
  loading             : false,
  brands              : [],
  brandsLoaded        : false,
  refreshBrands       : () => {},
  activeBrandId       : null,
  setActiveBrandId    : () => {},
  publishers          : [],
  activePublisherId   : null,
  setActivePublisherId: () => {},
  opsPaymentModel     : PAYMENT_MODEL.MANAGED_PAYOUT,
  setOpsPaymentModel  : () => {}
});

const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(
    () => getItemFromStore("userInfo") || {}
  );
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [activeBrandId, setActiveBrandIdState] = useState(
    () => getItemFromStore("activeBrandId") || null
  );
  const [publishers, setPublishers] = useState([]);
  const [activePublisherId, setActivePublisherIdState] = useState(
    () => getItemFromStore("activePublisherId") || null
  );
  // Which payment model the Ops workspace is looking at, driven by the topbar
  // toggle. Self-payout brands pay their publishers directly, so debit notes and
  // disbursements don't exist for them — that view drops both from the nav and
  // from the routes (see navigation/index.js and router/private-route.jsx).
  const [opsPaymentModel, setOpsPaymentModelState] = useState(
    () => getItemFromStore("opsPaymentModel") || PAYMENT_MODEL.MANAGED_PAYOUT
  );

  const { axiosInstance: brandAxiosInstance } = useAxios({ base: "brand" });
  const { axiosInstance: affiliateAxiosInstance } = useAxios({ base: "affiliate" });

  const handleUserData = (data) => {
    setUserData(data ?? {});
    setLoading(false);
  };

  const setActiveBrandId = (id) => {
    setActiveBrandIdState(id);
    setItemToStore("activeBrandId", id);
  };

  const setActivePublisherId = (id) => {
    setActivePublisherIdState(id);
    setItemToStore("activePublisherId", id);
  };

  const setOpsPaymentModel = (model) => {
    setOpsPaymentModelState(model);
    setItemToStore("opsPaymentModel", model);
  };

  // Brand-topbar switcher: fetches the brand accounts this session can act as
  // and defaults to the first one. Also carries each brand's workspace-setup
  // state, which the brand route guard uses to lock the workspace behind
  // /brand/:brandId/setup until the brand has answered it.
  // Exposed as refreshBrands() so the setup screen can re-read it after saving
  // instead of forcing a reload to lift the gate.
  const refreshBrands = async () => {
    const res = await brandAxiosInstance.get(BRAND_ENDPOINTS.DASHBOARD_ACCOUNTS);
    const result = res.data || res;
    const items = Array.isArray(result) ? result : (result.items || result.results || []);
    const list = items.map((brand) => ({
      id                : brand._id,
      name              : brand.name,
      kycStatus         : brand.kyc_status,
      paymentModel      : brand.payment_model || brand.payment_method || null,
      debitNoteFrequency: brand.debit_note_frequency ?? null,
      kycWindow         : brand.kyc_window ?? null,
      paymentSetupStatus: brand.payment_setup_status ?? null,
      setupPending      : isBrandSetupPending(brand),
      // A self-payout brand pays its publishers directly, so no debit note is
      // ever raised against it — the whole section is hidden for it.
      hasDebitNotes     : brand.payment_model !== PAYMENT_MODEL.SELF_PAYOUT
    }));

    setBrands(list);
    setBrandsLoaded(true);

    // Read the persisted id rather than the state closure — refreshBrands is
    // called from outside this render too (after the setup screen saves).
    const currentId = getItemFromStore("activeBrandId");
    if (!list.some((brand) => brand.id === currentId)) {
      const fallback = list[0]?.id || null;
      setActiveBrandIdState(fallback);
      setItemToStore("activeBrandId", fallback);
    }

    return list;
  };

  useEffect(() => {
    if (userData?.user?.role !== "brand") return;
    refreshBrands();
  }, [userData?.user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Publisher-topbar switcher: fetches the publisher accounts this session can act as
  // and defaults to the first one. Reuses the Ops "publishers/v1" list endpoint since
  // there's no dedicated publisher-scoped one yet.
  useEffect(() => {
    const role = userData?.user?.role;
    if (role !== "publisher") return;

    const applyList = (list) => {
      setPublishers(list);
      if (!list.some((publisher) => publisher.id === activePublisherId)) {
        const fallback = list[0]?.id || null;
        setActivePublisherIdState(fallback);
        setItemToStore("activePublisherId", fallback);
      }
    };

    const fetchPublishers = async () => {
      try {
        const res = await affiliateAxiosInstance.get(PUBLISHER_ENDPOINTS.DASHBOARD_ACCOUNTS);
        const result = res.data || res;
        const items = Array.isArray(result) ? result : (result.items || result.results || []);
        const list = items.map((publisher) => ({
          id         : publisher._id || publisher.publisher_id,
          name       : publisher.brand_name,
          kycStatus  : publisher.kyc_status,
          holder_name: publisher.holder_name
        }));

        applyList(list);
      } catch {
        applyList([]);
      }
    };
    fetchPublishers();
  }, [userData?.user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UserContext.Provider value={{
      userData,
      handleUserData,
      loading,
      brands,
      brandsLoaded,
      refreshBrands,
      activeBrandId,
      setActiveBrandId,
      publishers,
      activePublisherId,
      setActivePublisherId,
      opsPaymentModel,
      setOpsPaymentModel
    }}>
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };
